import React, { useMemo } from 'react';

const TopicProposalForm = ({
    isOpen, onClose, onSubmit,
    formData, setFormData,
    isEditing, isReadOnly,
    topicLevels, researchFields, toast
}) => {
    const parsedFiles = useMemo(() => {
        if (!formData.AttachmentUrl) return [];
        try {
            if (formData.AttachmentUrl.startsWith('[')) {
                return JSON.parse(formData.AttachmentUrl);
            }
            if (formData.AttachmentUrl.includes('|')) {
                const parts = formData.AttachmentUrl.split('|');
                return [{ name: parts[0], data: parts[1], description: '' }];
            }
        } catch (e) {
            return [];
        }
        return [];
    }, [formData.AttachmentUrl]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        if (isReadOnly) return;
        const { name, value, type } = e.target;
        setFormData({ ...formData, [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value });
    };

    const handleTitleBlur = async (e) => {
        if (isReadOnly) return;
        const titleText = e.target.value;
        if (!titleText || titleText.trim().length < 5) return;

        try {
            const userStr = localStorage.getItem('user');
            const currentUser = userStr ? JSON.parse(userStr) : null;
            const userId = currentUser ? currentUser.Id : '';

            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isManager = userRoleName === 'admin' || currentUser?.RoleId === 1 ||
                (userRoleName.includes('trưởng khoa') && currentUser?.IsDelegatedAdmin === true);

            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const response = await fetch(`${baseUrl}/topicproposal?userId=${userId}&isAdmin=${isManager}`, {
                headers: {
                    'X-User-Role-Id': currentUser?.RoleId?.toString() || '',
                    'X-User-Dept-Id': currentUser?.DepartmentId?.toString() || ''
                }
            });

            if (response.ok) {
                const existingData = await response.json();

                const normalizeString = (str) => {
                    if (!str) return '';
                    return str.toLowerCase().trim()
                        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                        .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                        .replace(/[^a-z0-9]/g, "");
                };

                const calculateSimilarity = (s, t) => {
                    if (!s || !t) return 0;
                    if (s === t) return 1;
                    const d = Array(s.length + 1).fill(null).map(() => Array(t.length + 1).fill(null));
                    for (let i = 0; i <= s.length; i++) d[i][0] = i;
                    for (let j = 0; j <= t.length; j++) d[0][j] = j;
                    for (let j = 1; j <= t.length; j++) {
                        for (let i = 1; i <= s.length; i++) {
                            const cost = s[i - 1] === t[j - 1] ? 0 : 1;
                            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
                        }
                    }
                    return 1.0 - (d[s.length][t.length] / Math.max(s.length, t.length));
                };

                const normInput = normalizeString(titleText);
                const currentId = isEditing ? formData.Id : 0;
                const inputYear = formData.ProposalYear ? parseInt(formData.ProposalYear) : 0;

                for (let item of existingData) {
                    if (item.Id === currentId) continue;

                    const normExisting = normalizeString(item.TopicName);
                    const sim = calculateSimilarity(normInput, normExisting);
                    const existingYear = item.ProposalYear ? parseInt(item.ProposalYear) : 0;

                    if (sim > 0.90) {
                        if (sim > 0.98 || existingYear === inputYear) {
                            toast?.current?.show({
                                severity: 'warn',
                                summary: 'Cảnh báo trùng lặp',
                                detail: `Hệ thống phát hiện Đề xuất NCKH rất giống đã tồn tại: "${item.TopicName}". Vui lòng kiểm tra lại!`,
                                life: 8000
                            });
                            break;
                        }
                    }
                }
            }
        } catch (err) {
            console.error("Lỗi check trùng lặp:", err);
        }
    };

    const updateEvidenceFiles = (newFiles) => {
        setFormData({ ...formData, AttachmentUrl: JSON.stringify(newFiles) });
    };

    const handleMultiFileChange = (e) => {
        if (isReadOnly) return;

        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
        const MAX_FILE_SIZE = 5 * 1024 * 1024;

        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const validFiles = files.filter(file => {
            const isTypeValid = allowedTypes.includes(file.type);
            const fileName = file.name.toLowerCase();
            const isExtensionValid = allowedExtensions.some(ext => fileName.endsWith(ext));

            if (!isTypeValid && !isExtensionValid) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Định dạng không hợp lệ',
                    detail: `Tệp "${file.name}" không được hỗ trợ. Chỉ cho phép PDF, JPG/JPEG, PNG`,
                    life: 5000
                });
                return false;
            }

            if (file.size > MAX_FILE_SIZE) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Dung lượng quá lớn',
                    detail: `Tệp "${file.name}" vượt quá giới hạn 5MB. Vui lòng chọn tệp nhỏ hơn.`,
                    life: 5000
                });
                return false;
            }

            return true;
        });

        if (validFiles.length === 0) {
            e.target.value = null;
            return;
        }

        const newFilesPromises = validFiles.map(file => new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve({ name: file.name, data: reader.result, description: '' });
            reader.readAsDataURL(file);
        }));

        Promise.all(newFilesPromises).then(newFiles => {
            updateEvidenceFiles([...parsedFiles, ...newFiles]);
        });
        e.target.value = null;
    };

    const handleFileDescChange = (index, desc) => {
        const updated = [...parsedFiles];
        updated[index].description = desc;
        updateEvidenceFiles(updated);
    };

    const removeEvidenceFile = (index) => {
        const updated = parsedFiles.filter((_, i) => i !== index);
        updateEvidenceFiles(updated);
    };

    const downloadIndividualFile = async (fileObj) => {
        let fileUrl = fileObj.url || fileObj.data || fileObj.link || fileObj.Link || fileObj.URL;
        if (!fileUrl) {
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không tìm thấy đường dẫn tệp!', life: 3000 });
            return;
        }

        const base64ToBlob = (dataUrl) => {
            const arr = dataUrl.split(',');
            const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            return new Blob([u8arr], { type: mime });
        };

        if (typeof fileUrl === 'string') {
            fileUrl = fileUrl.trim();
            if (fileUrl.startsWith('/due-bucket') || fileUrl.startsWith('/uploads')) {
                fileUrl = `https://data.tapchikhkt.vn${fileUrl}`;
            }
        }

        try {
            if (typeof fileUrl === 'string' && fileUrl.startsWith('data:')) {
                const blob = base64ToBlob(fileUrl);
                const objectUrl = URL.createObjectURL(blob);

                const newWindow = window.open(objectUrl, '_blank');
                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }

                setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
                return;
            }

            const newWindow = window.open(fileUrl, '_blank');
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error("Lỗi khi xử lý mở file:", error);
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể hiển thị tệp này do lỗi định dạng!', life: 3000 });
        }
    };

    const handleLocalSubmit = (e) => {
        e.preventDefault();
        if (isReadOnly) return;

        if (parsedFiles.length === 0) {
            toast?.current?.show({
                severity: 'warn',
                summary: 'Thiếu thông tin',
                detail: 'Vui lòng đính kèm ít nhất một Tệp tin đề xuất!',
                life: 4000
            });
            return;
        }

        onSubmit(e);
    };

    const qOptions = ["", "Q1", "Q2", "Q3", "Q4"];

    return (
        <div className="modal-overlay">
            <div className="modal-box form-modal-box" style={{ maxHeight: '95vh', overflowY: 'auto' }}>

                <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>
                        {isReadOnly ? "Chi tiết Đề xuất" : (isEditing ? "Cập nhật Đề xuất" : "Thêm mới Đề xuất")}
                    </h3>
                    <button className="close-btn" onClick={onClose} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>
                        &times;
                    </button>
                </div>

                <div className="modal-body" style={{ padding: '25px' }}>
                    <form id="propForm" onSubmit={handleLocalSubmit}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '15px' }}>
                            <div className="form-group" style={{ flex: '1 1 150px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Năm đề xuất *</label>
                                <input type="number" name="ProposalYear" className="form-input" value={formData.ProposalYear || ''} onChange={handleChange} required disabled={isReadOnly} />
                            </div>
                            <div className="form-group" style={{ flex: '2 1 300px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Cấp đề tài *</label>
                                <select name="TopicLevelId" className="form-input" value={formData.TopicLevelId || ''} onChange={handleChange} required disabled={isReadOnly}>
                                    <option value="">Chọn cấp đề tài</option>
                                    {topicLevels.map(lvl => <option key={lvl.Id} value={lvl.Id}>{lvl.Name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Lĩnh vực *</label>
                            <select name="ResearchFieldId" className="form-input" value={formData.ResearchFieldId || ''} onChange={handleChange} required disabled={isReadOnly}>
                                <option value="">Chọn lĩnh vực</option>
                                {researchFields.map(rf => <option key={rf.Id} value={rf.Id}>{rf.Name}</option>)}
                            </select>
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tên đề tài *</label>
                            <input
                                type="text"
                                name="TopicName"
                                className="form-input"
                                value={formData.TopicName || ''}
                                onChange={handleChange}
                                onBlur={handleTitleBlur}
                                required
                                disabled={isReadOnly}
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tính cấp thiết *</label>
                            <textarea name="Urgency" className="form-input" rows="2" value={formData.Urgency || ''} onChange={handleChange} required disabled={isReadOnly} style={{ resize: 'vertical' }}></textarea>
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Mục tiêu *</label>
                            <textarea name="Objectives" className="form-input" rows="2" value={formData.Objectives || ''} onChange={handleChange} required disabled={isReadOnly} style={{ resize: 'vertical' }}></textarea>
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nội dung chính *</label>
                            <textarea name="MainContent" className="form-input" rows="3" value={formData.MainContent || ''} onChange={handleChange} required disabled={isReadOnly} style={{ resize: 'vertical' }}></textarea>
                        </div>

                        <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px', marginBottom: '20px', border: '1px solid #eee' }}>
                            <h4 style={{ margin: '0 0 15px 0', color: '#2c3e50' }}>Cam kết Bài báo & Sách</h4>
                            <div className="form-grid-2">
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                    <div className="form-group" style={{ flex: '1 1 120px' }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Bài SCIE/SSCI/AHCI *</label>
                                        <input type="number" name="ScieArticleCount" className="form-input" value={formData.ScieArticleCount ?? ''} onChange={handleChange} required disabled={isReadOnly} />
                                    </div>
                                    <div className="form-group" style={{ flex: '1 1 120px' }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Thuộc Q1, Q2, Q3, Q4</label>
                                        <select name="ScieQCategory" className="form-input" value={formData.ScieQCategory || ''} onChange={handleChange} disabled={isReadOnly}>
                                            {qOptions.map(q => <option key={q} value={q}>{q || 'Chọn Q'}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                    <div className="form-group" style={{ flex: '1 1 120px' }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Bài Scopus *</label>
                                        <input type="number" name="ScopusArticleCount" className="form-input" value={formData.ScopusArticleCount ?? ''} onChange={handleChange} required disabled={isReadOnly} />
                                    </div>
                                    <div className="form-group" style={{ flex: '1 1 120px' }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Thuộc Q1, Q2, Q3, Q4</label>
                                        <select name="ScopusQCategory" className="form-input" value={formData.ScopusQCategory || ''} onChange={handleChange} disabled={isReadOnly}>
                                            {qOptions.map(q => <option key={q} value={q}>{q || 'Chọn Q'}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Bài báo trên tạp chí ESCI *</label>
                                    <input type="number" name="EsciArticleCount" className="form-input" value={formData.EsciArticleCount ?? ''} onChange={handleChange} required disabled={isReadOnly} />
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Kỷ yếu HT/HN danh mục WoS/Scopus *</label>
                                    <input type="number" name="ConferenceProceedingCount" className="form-input" value={formData.ConferenceProceedingCount ?? ''} onChange={handleChange} required disabled={isReadOnly} />
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Bài báo nước ngoài khác *</label>
                                    <input type="number" name="OtherForeignArticleCount" className="form-input" value={formData.OtherForeignArticleCount ?? ''} onChange={handleChange} required disabled={isReadOnly} />
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Bài báo trong nước (danh mục HĐGSNN) *</label>
                                    <input type="number" name="DomesticArticleCount" className="form-input" value={formData.DomesticArticleCount ?? ''} onChange={handleChange} required disabled={isReadOnly} />
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Sách xuất bản *</label>
                                    <input type="number" name="PublishedBookCount" className="form-input" value={formData.PublishedBookCount ?? ''} onChange={handleChange} required disabled={isReadOnly} />
                                </div>
                            </div>
                        </div>

                        <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px', marginBottom: '20px', border: '1px solid #eee' }}>
                            <h4 style={{ margin: '0 0 15px 0', color: '#2c3e50' }}>Cam kết Đào tạo</h4>
                            <div className="form-grid-2">
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Số học viên cao học *</label>
                                    <input type="number" name="MasterStudentCount" className="form-input" value={formData.MasterStudentCount ?? ''} onChange={handleChange} required disabled={isReadOnly} />
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Số nghiên cứu sinh *</label>
                                    <input type="number" name="PhdStudentCount" className="form-input" value={formData.PhdStudentCount ?? ''} onChange={handleChange} required disabled={isReadOnly} />
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Số hướng dẫn SVNCKH *</label>
                                    <input type="number" name="UndergradResearchCount" className="form-input" value={formData.UndergradResearchCount ?? ''} onChange={handleChange} required disabled={isReadOnly} />
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Hướng dẫn KLTN đại học *</label>
                                    <input type="number" name="UndergradThesisCount" className="form-input" value={formData.UndergradThesisCount ?? ''} onChange={handleChange} required disabled={isReadOnly} />
                                </div>
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Sản phẩm ứng dụng</label>
                            <textarea name="AppliedProducts" className="form-input" rows="2" value={formData.AppliedProducts || ''} onChange={handleChange} disabled={isReadOnly} style={{ resize: 'vertical' }}></textarea>
                        </div>
                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Sản phẩm khác</label>
                            <textarea name="OtherProducts" className="form-input" rows="2" value={formData.OtherProducts || ''} onChange={handleChange} disabled={isReadOnly} style={{ resize: 'vertical' }}></textarea>
                        </div>
                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Địa chỉ ứng dụng *</label>
                            <input type="text" name="ApplicationAddress" className="form-input" value={formData.ApplicationAddress || ''} onChange={handleChange} required disabled={isReadOnly} />
                        </div>
                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Hiệu quả mang lại *</label>
                            <textarea name="ExpectedEfficiency" className="form-input" rows="2" value={formData.ExpectedEfficiency || ''} onChange={handleChange} required disabled={isReadOnly} style={{ resize: 'vertical' }}></textarea>
                        </div>

                        <div className="form-grid-2" style={{ marginBottom: '20px' }}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Kinh phí (triệu VNĐ) *</label>
                                <input type="number" step="0.1" name="Budget" className="form-input" value={formData.Budget ?? ''} onChange={handleChange} required disabled={isReadOnly} />
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Thời gian nghiên cứu (tháng) *</label>
                                <input type="number" name="DurationMonths" className="form-input" value={formData.DurationMonths ?? ''} onChange={handleChange} required disabled={isReadOnly} />
                            </div>
                        </div>

                        {!isReadOnly && (
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>Tệp tin đề xuất (File Base64) *</label>

                                <p style={{ color: '#e74c3c', fontStyle: 'italic', marginBottom: '15px', marginTop: '-5px', fontSize: '14px' }}>
                                    * Chú ý: Hệ thống chỉ nhận tệp minh chứng ở định dạng PDF, JPG/JPEG và PNG. Dung lượng tối đa 5MB / tệp
                                </p>

                                {parsedFiles.length > 0 && (
                                    <div style={{ overflowX: 'auto', marginBottom: '15px' }}>
                                        <table className="custom-table" style={{ width: '100%', minWidth: '400px', margin: 0, border: '1px solid #eee' }}>
                                            <thead style={{ backgroundColor: '#f9f9f9' }}>
                                                <tr>
                                                    <th width="50px" style={{ textAlign: 'center' }}>TT</th>
                                                    <th>Mô tả</th>
                                                    <th width="35%">Minh chứng</th>
                                                    <th width="80px" style={{ textAlign: 'center' }}>Chức năng</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {parsedFiles.map((file, idx) => (
                                                    <tr key={idx}>
                                                        <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                        <td>
                                                            <input type="text" className="form-input" placeholder="Nhập thông tin mô tả" value={file.description || ''} onChange={(e) => handleFileDescChange(idx, e.target.value)} style={{ width: '100%', padding: '8px', border: 'none', background: 'transparent', borderBottom: '1px solid #eee', borderRadius: 0 }} />
                                                        </td>
                                                        <td>
                                                            <span onClick={() => downloadIndividualFile(file)} style={{ color: '#3498db', cursor: 'pointer', wordBreak: 'break-all' }} title="Nhấn để tải về">{file.name}</span>
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button type="button" onClick={() => removeEvidenceFile(idx)} style={{ background: '#fff', border: '1px solid #e74c3c', color: '#e74c3c', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}><i className="fa-solid fa-trash-can"></i></button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <div style={{ border: '2px dashed #3498db', borderRadius: '8px', padding: '30px', textAlign: 'center', backgroundColor: '#ebf5fb', position: 'relative', overflow: 'hidden' }}>
                                    <input
                                        type="file"
                                        multiple
                                        accept=".pdf, .jpg, .jpeg, .png, application/pdf, image/jpeg, image/png"
                                        onChange={handleMultiFileChange}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 10 }}
                                        title="Nhấn để chọn tệp"
                                    />
                                    {parsedFiles.length > 0 ? (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '35px', color: '#3498db', marginBottom: '10px' }}></i>
                                            <p style={{ margin: '0 0 10px 0', color: '#003399', fontWeight: 'bold' }}>Đã có {parsedFiles.length} tệp đính kèm</p>
                                            <button type="button" style={{ padding: '6px 15px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', color: '#333' }}>Thêm tệp tin khác</button>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '40px', color: '#3498db', marginBottom: '10px' }}></i>
                                            <p style={{ margin: '0 0 10px 0', color: '#003399', fontWeight: 'bold' }}>Chưa có tệp nào được chọn</p>
                                            <button type="button" style={{ padding: '6px 15px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', color: '#333' }}>Chọn tệp từ thiết bị</button>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {isReadOnly && formData.AttachmentUrl && (
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>Tệp tin đính kèm</label>
                                {parsedFiles.length > 0 ? (
                                    <div style={{ overflowX: 'auto' }}>
                                        <table className="custom-table" style={{ width: '100%', minWidth: '400px', border: '1px solid #eee' }}>
                                            <thead style={{ backgroundColor: '#f9f9f9' }}>
                                                <tr>
                                                    <th width="50px" style={{ textAlign: 'center' }}>TT</th>
                                                    <th>Mô tả</th>
                                                    <th width="35%">Tệp tin</th>
                                                    <th width="80px" style={{ textAlign: 'center' }}>Chức năng</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {parsedFiles.map((file, idx) => (
                                                    <tr key={idx}>
                                                        <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                        <td>{file.description || '---'}</td>
                                                        <td><span onClick={() => downloadIndividualFile(file)} style={{ color: '#3498db', cursor: 'pointer', wordBreak: 'break-all' }} title="Nhấn để tải về">{file.name}</span></td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button type="button" onClick={() => downloadIndividualFile(file)} style={{ background: '#fff', border: '1px solid #3498db', color: '#3498db', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}><i className="fa-solid fa-download"></i></button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div style={{ padding: '15px', backgroundColor: '#ebf5fb', border: '1px solid #bce0fd', borderRadius: '5px', color: '#0056b3', fontWeight: '500' }}>
                                        <i className="fa-solid fa-file-contract" style={{ marginRight: '10px' }}></i>{parsedFiles[0]?.name || 'Có tệp đính kèm'}
                                    </div>
                                )}
                            </div>
                        )}

                    </form>
                </div>

                <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 25px' }}>
                    <button type="button" className="btn-cancel" onClick={onClose}>
                        {isReadOnly ? "Đóng" : "Hủy"}
                    </button>
                    {!isReadOnly && (
                        <button type="submit" form="propForm" className="btn-submit">Hoàn tất</button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TopicProposalForm;